import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useContext,useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import styled from "styled-components";
import { UserContext } from "../../../context/UserContext";
import AttToken from "../../../helpers/attToken";
import { trackEventMatomo } from "../../../helpers/matomo";
import PlanoDeAcao from "../../../service/PlanoDeAcao";
import queryClient from "../../../service/query";
import { propsModalType } from "../../../types/plano-de-acao/types";
import ModalImagePreview from "../../Reports/Modal/ModalImagePreview";
import { uploadFilesV2 } from "../../helpers/checklists";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type props = {
  open: boolean;
  setOpen: Function;
  propsModal: propsModalType;
};

const planoService = new PlanoDeAcao();

export default function EditaStatus({ open, setOpen, propsModal }: props) {
  const { t } = useTranslation("translation");
  const { userData } = useContext(UserContext);
  const [status, setStatus] = useState<string>(propsModal.statusTask);
  const [comentario, setComentario] = useState<string>("");
  const [image, setImage] = useState<File[]>([]);
  const [arquivo, setArquivo] = useState<File[]>([]);
  const [openImage, setOpenImage] = useState<boolean>(false);
  const [fileType, setFileType] = useState<string>("");

  async function convertFiles(files: File[], id: string) {
    const arquivoConvertido: File[] = [];
    let referencia = "";

    for (const file of files) {
      const fileType = file.name.substring(file.name.lastIndexOf(".") + 1);
      const newName = `${crypto.randomUUID()}.${fileType}`;
      const newFile = new File([file], newName, { type: file.type });

      arquivoConvertido.push(newFile);
      referencia = `planoAcao/${id}/${newName}`;
    }

    return { arquivoConvertido, referencia };
  }

  async function convertImages(files: File[], id: string) {
    const imagemConvertida: File[] = [];
    let referencia: string = "";

    for (const file of files) {
      const newName = `${crypto.randomUUID()}.webp`;
      const newFile = new File([file], newName, { type: "webp" });

      imagemConvertida.push(newFile);
      referencia = `planoAcao/${id}/${newName}`;
    }

    return { imagemConvertida, referencia };
  }

  const { mutate, isPending: isLoading } = useMutation({
    mutationKey: ["EDIT_PLANO_STATUS"],
    mutationFn: async () => {
      const token = await AttToken();
      if (token) {
        const allFiles: File[] = [];
        const acao = {
          taskIndex: propsModal.taskIndex,
          comentario,
          status,
          planoId: propsModal.planoId,
          foto: "",
        };
        if (arquivo?.length) {
          const { arquivoConvertido, referencia } = await convertFiles(arquivo, propsModal.planoId);
          allFiles.push(...arquivoConvertido);
          acao.foto = referencia;
        }

        if (image?.length) {
          const { imagemConvertida, referencia } = await convertImages(image, propsModal.planoId);
          allFiles.push(...imagemConvertida);
          acao.foto = referencia;
        }
        if (allFiles.length > 0) {
          uploadFilesV2(allFiles, `planoAcao/${token.username}/${acao.planoId}/`);
        }

        const response = await planoService.changeStatusTask(token, acao);
        return response;
      }
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: ["GET_PLANOS_ACAO"] });
      setOpen(!open);
      toast.success(`${t("ActionPlan.Modal.statusChanged")}`);
    },
    onError: () => {
      toast.error(`${t("ActionPlan.Modal.errorChangingStatus")}`);
    },
  });

  const verifyDisable = () => {
    if (isLoading) {
      return true;
    }
    if (userData.userName !== propsModal.responsavelNome) {
      if ((propsModal.obrigatorioEvidencia && !comentario?.length) || (propsModal.obrigatorioEvidencia && !image?.length) || status === "pendente") {
        return true;
      }
    }

    return false;
  };

  const getFileType = (value: string) => {
    const fileName = value.substr(12, value?.length);
    const extension = fileName.split(".").pop();
    const imageTypes = ["JPEG", "JPG", "BMP", "GIF", "TIFF", "HEIF", "RAW", "PSD", "SVG", "PNG"];
    if (imageTypes?.find((el) => el === String(extension).toUpperCase())) {
      return (
        <Stack direction="column" alignItems="center" justifyContent="flex-start" spacing={1}>
          <Card sx={{ width: 200, height: 200 }}>
            <IconButton
              onClick={(e) => {
                trackEventMatomo("modal edita status PA", "click", "button", "deleta imagem");
                e.preventDefault();
                setImage([]);
              }}
              color="error">
              <FaTrash />
            </IconButton>
            <CardActionArea
              onClick={() => {
                trackEventMatomo("modal edita status PA", "click", "button", "abre imagem");
                setOpenImage(!openImage);
              }}
              sx={{ padding: 1 }}>
              <CardMedia
                sx={{ maxHeight: 150, maxWidth: 150, objectFit: "contain" }}
                component="img"
                image={URL.createObjectURL(image[0])}
                alt={fileName}
              />
            </CardActionArea>
          </Card>
          <Chip label={fileName.length > 20 ? fileName.slice(0, 20).concat("...") : fileName} avatar={<Avatar>{extension}</Avatar>} />
        </Stack>
      );
    } else {
      return (
        <Stack direction="column" alignItems="center" justifyContent="flex-start" spacing={1}>
          <Paper sx={{ width: "200px", height: "200px" }}>
            <IconButton
              onClick={() => {
                trackEventMatomo("modal edita status PA", "click", "button", "deleta imagem");
                setArquivo([]);
              }}
              color="error">
              <FaTrash />
            </IconButton>
            <Stack alignItems="center" justifyContent="center" sx={{ position: "relative" }}>
              <Typography color="textDisabled" fontWeight={600} position="absolute">
                {extension}
              </Typography>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 150, padding: 0 }} fontSize="large" />
            </Stack>
          </Paper>
          <Tooltip title={fileName}>
            <Chip label={fileName.length > 20 ? fileName.slice(0, 20).concat("...") : fileName} avatar={<Avatar>{extension}</Avatar>} />
          </Tooltip>
        </Stack>
      );
    }
  };

  return (
    <>
      {openImage && <ModalImagePreview image={String(image)} open={openImage} setOpen={setOpenImage} />}
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={() => {
          setOpen(!open);
          trackEventMatomo("modal edita status PA", "click", "button", "abre modal");
        }}
        scroll="paper">
        <DialogTitle>Editar status</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => {
            setOpen(!open);
            trackEventMatomo("modal edita status PA", "click", "button", "abre modal");
          }}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}>
          <CloseIcon />
        </IconButton>
        <DialogContent dividers={true}>
          <Stack spacing={1.5} flexDirection="column" alignItems="flex-start" justifyContent="flex-start">
            <FormControl>
              <RadioGroup aria-labelledby="demo-radio-buttons-group-label" defaultValue="female" name="radio-buttons-group">
                {propsModal?.logado === propsModal?.responsavel && (
                  <>
                    <FormControlLabel
                      disabled
                      value="pendente"
                      checked={status === "pendente"}
                      name="status"
                      control={<Radio />}
                      onChange={() => {
                        trackEventMatomo("modal edita status PA", "click", "radio", "edita valor");
                        setStatus("pendente");
                      }}
                      label={
                        <>
                          <Chip sx={{ width: "110px" }} color="warning" label={t("ActionPlan.Modal.pending")} />
                          {status === "pendente" && (
                            <Tooltip title={t("ActionPlan.Modal.statusMustNotBePending")}>
                              <InfoIcon color="error" />
                            </Tooltip>
                          )}
                        </>
                      }
                    />
                    <FormControlLabel
                      checked={status === "emExecucao"}
                      name="status"
                      value="emExecucao"
                      control={<Radio />}
                      label={<Chip sx={{ width: "110px" }} color="info" label={t("ActionPlan.Modal.inExecution")} />}
                      onChange={() => {
                        trackEventMatomo("modal edita status PA", "click", "radio", "edita valor");
                        setStatus("emExecucao");
                      }}
                    />
                    <FormControlLabel
                      checked={status === "concluido"}
                      name="status"
                      value="concluido"
                      control={<Radio />}
                      label={<Chip sx={{ width: "110px" }} disabled color="success" label={t("ActionPlan.Modal.completed")} />}
                      onChange={() => {
                        trackEventMatomo("modal edita status PA", "click", "radio", "edita valor");
                        setStatus("concluido");
                      }}
                    />
                  </>
                )}
                {propsModal?.logado === propsModal?.criador && (
                  <>
                    <FormControlLabel
                      checked={status === "aprovado"}
                      name="status"
                      value="aprovado"
                      control={<Radio />}
                      disabled={propsModal.statusTask !== "concluido"}
                      label={<Chip sx={{ width: "110px" }} color="success" label={t("ActionPlan.Modal.approved")} />}
                      onChange={() => {
                        trackEventMatomo("modal edita status PA", "click", "radio", "edita valor");
                        setStatus("aprovado");
                      }}
                    />
                    <FormControlLabel
                      checked={status === "rejeitado"}
                      name="status"
                      value="rejeitado"
                      control={<Radio />}
                      label={<Chip sx={{ width: "110px" }} color="error" label={t("ActionPlan.Modal.rejected")} />}
                      disabled={propsModal.statusTask !== "concluido"}
                      onChange={() => {
                        trackEventMatomo("modal edita status PA", "click", "radio", "edita valor");
                        setStatus("rejeitado");
                      }}
                    />
                  </>
                )}
              </RadioGroup>
            </FormControl>
            <Badge sx={{ width: "100%" }}>
              {userData.userName !== propsModal.responsavelNome && propsModal.obrigatorioEvidencia && comentario === "" && (
                <Tooltip title={t("ActionPlan.Modal.commentRequired")}>
                  <InfoIcon color="error" />
                </Tooltip>
              )}
              <TextField
                required={userData.userName !== propsModal.responsavelNome && propsModal.obrigatorioEvidencia}
                fullWidth
                label={<>{t("ActionPlan.Modal.addComment")}</>}
                multiline
                rows={4}
                maxRows={4}
                value={comentario}
                onChange={(event) => {
                  trackEventMatomo("modal edita status PA", "click", "textarea", "edita valor");
                  setComentario(event.target.value);
                }}
              />
            </Badge>
            {userData.userName !== propsModal.responsavelNome && (
              <>
                {!image.length && !arquivo.length ? (
                  <>
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%" spacing={1}>
                      <Button component="label" variant="contained" endIcon={<UploadFileIcon />}>
                        Enviar arquivo
                        <VisuallyHiddenInput
                          accept="video/*, .doc, .docx, .xls, .xlsx, audio/*, .pdf, .txt"
                          type="file"
                          onChange={(event) => {
                            if (!event.target.files) return;
                            setFileType(event.target.value);
                            const novos = Array.from(event.target.files);
                            const tamanhoMaxIndividual = 20 * 1024 * 1024; // 20MB

                            const validos = novos.filter((file) => {
                              if (file.size > tamanhoMaxIndividual) {
                                toast.warning(`O arquivo "${file.name}" ultrapassa 20MB e foi ignorado.`);
                                return false;
                              }
                              return true;
                            });
                            setArquivo(validos);
                          }}
                        />
                      </Button>
                      <Button color="secondary" component="label" variant="contained" endIcon={<AddPhotoAlternateIcon />}>
                        Enviar foto
                        <VisuallyHiddenInput
                          accept="image/jpg, .jpeg, .png, .gif, .tiff, .tif, .svg, .webp, .heic, .heif, .bmp"
                          type="file"
                          onChange={(event) => {
                            if (!event.target.files) return;
                            setFileType(event.target.value);
                            const novos = Array.from(event.target.files);
                            const tamanhoMaxIndividual = 20 * 1024 * 1024; // 20MB

                            const validos = novos.filter((file) => {
                              const tipoPermitido =
                                /^(image\/jpg|image\/jpeg|image\/png|image\/gif|image\/tiff|image\/svg\+xml|image\/webp|image\/heic|image\/heif|image\/bmp)$/i;
                              if (!tipoPermitido.test(file.type)) {
                                toast.warning(
                                  `O arquivo "${file.name}" tem um tipo não aceito. Tipos permitidos: JPG, JPEG, PNG, GIF, TIFF, SVG, WebP, HEIC, HEIF, BMP.`,
                                );
                                return false;
                              }
                              if (file.size > tamanhoMaxIndividual) {
                                toast.warning(`A imagem "${file.name}" ultrapassa 20MB e foi ignorado.`);
                                return false;
                              }
                              return true;
                            });
                            setImage(validos);
                          }}
                        />
                      </Button>
                    </Stack>
                    <Typography color="textDisabled" fontSize="small" fontWeight={600}>
                      Tipos de imagem suportados: .jpg, .jpeg, .png, .gif, .tiff, .tif, .svg, .webp, .heic, .heif, .bmp
                    </Typography>
                    <Typography color="textDisabled" fontSize="small" fontWeight={600}>
                      Tipos de arquivos suportados: video, .doc, .docx, .xls, .xlsx, mp3, mp4, .pdf, .txt"
                    </Typography>
                  </>
                ) : (
                  <>{getFileType(fileType)}</>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={verifyDisable()}
            onClick={() => {
              trackEventMatomo("modal edita status PA", "click", "button", "salva");
              mutate();
            }}>
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
